import * as React from "react";
import { useMemo, useState } from "react";
import { line, scaleLinear } from "d3";
import { trainingUserMistakeService } from "../../../../modules/training/services/TrainingUserMistakeService";
const WIDTH = 900;
const HEIGHT = 330;
const LEFT = 60;
const RIGHT = 20;
const TOP = 20;
const BOTTOM = 70;
const STAGE_TOOLTIP_WIDTH = 240;
function wrapTooltipText(text, maxLength) {
    const words = text.split(/\s+/).flatMap((word) => {
        const chunks = [];
        for (let index = 0; index < word.length; index += maxLength) {
            chunks.push(word.slice(index, index + maxLength));
        }
        return chunks;
    });
    const lines = [];
    let line = "";
    for (const word of words) {
        if (line.length > 0 && line.length + word.length + 1 > maxLength) {
            lines.push(line);
            line = "";
        }
        line = line.length > 0 ? `${line} ${word}` : word;
    }
    if (line.length > 0)
        lines.push(line);
    return lines;
}
export function TrainingSpeedChart({ objectName, stats }) {
    var _a;
    const [hoveredViolation, setHoveredViolation] = useState();
    const [hoveredStage, setHoveredStage] = useState();
    const chart = useMemo(() => {
        var _a;
        if (stats.samples.length === 0)
            return undefined;
        const changes = (_a = stats.stageChanges) !== null && _a !== void 0 ? _a : [];
        let start = Number.POSITIVE_INFINITY;
        let end = Number.NEGATIVE_INFINITY;
        let maximum = 0;
        for (const sample of stats.samples) {
            start = Math.min(start, sample.timestamp);
            end = Math.max(end, sample.timestamp);
            maximum = Math.max(maximum, sample.speed);
        }
        for (const violation of stats.violations) {
            start = Math.min(start, violation.timestamp);
            end = Math.max(end, violation.timestamp + violation.durationMs);
        }
        for (const change of changes) {
            start = Math.min(start, change.timestamp);
            end = Math.max(end, change.timestamp);
        }
        const x = scaleLinear()
            .domain([0, Math.max((end - start) / 1000, 1)])
            .range([LEFT, WIDTH - RIGHT]);
        const y = scaleLinear()
            .domain([0, Math.max(maximum, stats.threshold, 0.1) * 1.1])
            .nice()
            .range([HEIGHT - BOTTOM, TOP]);
        const path = line()
            .x((sample) => x((sample.timestamp - start) / 1000))
            .y((sample) => y(sample.speed));
        return { changes, start, x, y, path };
    }, [stats]);
    if (chart === undefined)
        return React.createElement("p", { className: "end-training-empty" }, "Keine Geschwindigkeitsmessungen vorhanden.");
    const activeViolation = hoveredViolation === undefined ? undefined : stats.violations[hoveredViolation];
    const activeViolationElapsed = activeViolation === undefined ? 0 : (activeViolation.timestamp - chart.start) / 1000;
    const tooltipWidth = 220;
    const tooltipX = activeViolationElapsed > chart.x.domain()[1] / 2 ? WIDTH - RIGHT - tooltipWidth : LEFT;
    const activeStage = hoveredStage === undefined ? undefined : chart.changes[hoveredStage];
    const activeStageX = activeStage === undefined ? 0 : chart.x((activeStage.timestamp - chart.start) / 1000);
    const stageTooltipX = activeStageX > WIDTH / 2 ? WIDTH - RIGHT - tooltipWidth : LEFT;
    return (React.createElement("div", { className: "end-training-speed-chart" },
        React.createElement("div", { className: "end-training-chart-scroll" },
            React.createElement("svg", { viewBox: `0 0 ${WIDTH} ${HEIGHT}`, role: "img", "aria-label": `Geschwindigkeitsverlauf: ${objectName}, mit Grenzwert und markierten Überschreitungen` },
                stats.violations.map((violation, index) => {
                    const x = chart.x((violation.timestamp - chart.start) / 1000);
                    const endX = chart.x((violation.timestamp + violation.durationMs - chart.start) / 1000);
                    return (React.createElement("rect", { key: `episode-${index}`, x: x, y: TOP, width: endX - x, height: HEIGHT - BOTTOM - TOP, className: "end-training-chart-episode", "data-ui3d-custom-hover": "", onMouseOver: () => setHoveredViolation(index), onMouseOut: () => setHoveredViolation(undefined) }));
                }),
                chart.y.ticks(5).map((tick) => (React.createElement("g", { key: tick },
                    React.createElement("line", { x1: LEFT, x2: WIDTH - RIGHT, y1: chart.y(tick), y2: chart.y(tick), className: "end-training-chart-grid" }),
                    React.createElement("text", { x: LEFT - 8, y: chart.y(tick) + 4, textAnchor: "end" }, tick.toFixed(1))))),
                React.createElement("text", { x: LEFT, y: 12 }, "m/s"),
                React.createElement("g", { className: "end-training-chart-threshold", "data-tooltip": `Schwellenwert: ${stats.threshold.toFixed(2)} m/s`, "data-tooltip-position": "top-left" },
                    React.createElement("line", { x1: LEFT, x2: WIDTH - RIGHT, y1: chart.y(stats.threshold), y2: chart.y(stats.threshold), stroke: "currentColor", strokeDasharray: "8 4", strokeWidth: 1.5 }),
                    React.createElement("text", { x: WIDTH - RIGHT - 4, y: chart.y(stats.threshold) - 6, textAnchor: "end" }, `Schwellenwert: ${stats.threshold.toFixed(2)} m/s`)),
                React.createElement("path", { d: (_a = chart.path(stats.samples)) !== null && _a !== void 0 ? _a : undefined, fill: "none", stroke: "#1976d2", strokeWidth: 1.5 }),
                React.createElement("line", { x1: LEFT, x2: WIDTH - RIGHT, y1: HEIGHT - BOTTOM, y2: HEIGHT - BOTTOM, stroke: "currentColor" }),
                chart.x.ticks(5).map((tick) => (React.createElement("g", { key: tick },
                    React.createElement("line", { x1: chart.x(tick), x2: chart.x(tick), y1: HEIGHT - BOTTOM, y2: HEIGHT - BOTTOM + 5, stroke: "currentColor" }),
                    React.createElement("text", { x: chart.x(tick), y: HEIGHT - BOTTOM + 21, textAnchor: "middle" }, tick.toFixed(1))))),
                React.createElement("text", { x: (LEFT + WIDTH - RIGHT) / 2, y: HEIGHT - 4, textAnchor: "middle" }, "Zeit seit Trainingsbeginn (s)"),
                chart.changes.map((change, index) => {
                    const x = chart.x((change.timestamp - chart.start) / 1000);
                    const elapsed = (change.timestamp - chart.start) / 1000;
                    const stageLabelLines = wrapTooltipText(trainingUserMistakeService.getStageLabel(change.stage), 30);
                    const stageTooltipHeight = 32 + (stageLabelLines.length + 1) * 16;
                    const stageTooltipX = x > WIDTH / 2 ? WIDTH - RIGHT - STAGE_TOOLTIP_WIDTH : LEFT;
                    return (React.createElement("g", { key: `stage-${change.timestamp}-${index}`, className: "end-training-chart-stage-marker", "data-ui3d-custom-hover": "", onMouseOver: () => setHoveredStage(index), onMouseOut: () => setHoveredStage(undefined) },
                        hoveredStage === index && React.createElement("line", { x1: x, x2: x, y1: TOP, y2: HEIGHT - BOTTOM, className: "end-training-chart-stage", pointerEvents: "none" }),
                        React.createElement("circle", { cx: x, cy: HEIGHT - BOTTOM, r: 9, fill: "transparent" }),
                        React.createElement("circle", { cx: x, cy: HEIGHT - BOTTOM, r: 4, className: "end-training-chart-stage-dot" }),
                        hoveredStage === index && (React.createElement("g", { className: "end-training-chart-tooltip", pointerEvents: "none" },
                            React.createElement("rect", { x: stageTooltipX, y: TOP + 8, width: STAGE_TOOLTIP_WIDTH, height: stageTooltipHeight, rx: 4 }),
                            React.createElement("text", { x: stageTooltipX + 10, y: TOP + 26 },
                                React.createElement("tspan", { x: stageTooltipX + 10, dy: 0 }, "Stagewechsel"),
                                stageLabelLines.map((labelLine, lineIndex) => (React.createElement("tspan", { key: lineIndex, x: stageTooltipX + 10, dy: 16 }, labelLine))),
                                React.createElement("tspan", { x: stageTooltipX + 10, dy: 16 },
                                    "Zeitpunkt: ",
                                    elapsed.toFixed(2),
                                    " s"))))));
                }),
                activeViolation !== undefined && (React.createElement("g", { className: "end-training-chart-tooltip", pointerEvents: "none" },
                    React.createElement("rect", { x: tooltipX, y: TOP + 8, width: tooltipWidth, height: 72, rx: 4 }),
                    React.createElement("text", { x: tooltipX + 10, y: TOP + 26 },
                        React.createElement("tspan", { x: tooltipX + 10, dy: 0 },
                            "\u00DCberschreitung ",
                            (hoveredViolation !== null && hoveredViolation !== void 0 ? hoveredViolation : 0) + 1),
                        React.createElement("tspan", { x: tooltipX + 10, dy: 16 },
                            "Beginn: ",
                            activeViolationElapsed.toFixed(2),
                            " s"),
                        React.createElement("tspan", { x: tooltipX + 10, dy: 16 },
                            "Dauer: ",
                            (activeViolation.durationMs / 1000).toFixed(2),
                            " s"),
                        React.createElement("tspan", { x: tooltipX + 10, dy: 16 },
                            "Maximum: ",
                            activeViolation.speed.toFixed(2),
                            " m/s")))))),
        stats.violations.length > 0 && (React.createElement("p", { className: "end-training-chart-episode-legend" }, "Rote Bereiche markieren \u00DCberschreitungen. Darauf zeigen, um Beginn, Dauer und Maximum zu sehen."))));
}
